import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTime12, isLate, isEarly } from "@/lib/utils";
import { Users, GraduationCap, LogIn, LogOut, Activity, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const DAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const h24 = now.getHours();
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 || 12;
  const hh = String(h12).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return (
    <div className="bg-navy text-white rounded-md px-5 py-3 inline-flex items-center gap-3" data-testid="live-clock">
      <Clock className="w-4 h-4 text-emerald-300" />
      <div>
        <div className="font-mono font-bold text-2xl tracking-[0.15em] leading-none">{hh}:{mm}<span className="text-emerald-300">:{ss}</span> <span className="text-emerald-300 text-lg">{ampm}</span></div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-300 mt-1">{now.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" })}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: I, testid }) {
  return (
    <div className="bg-white border border-nardo-200 p-6 rounded-md relative" data-testid={testid}>
      <div className="absolute top-0 left-0 h-[2px] w-12 bg-navy-light" />
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">{label}</span>
        <I className="w-4 h-4 text-navy-light" strokeWidth={1.5} />
      </div>
      <div className="font-display font-extrabold text-4xl text-navy">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const lateFor = (k) => (k === "teacher" ? user?.late_after_teacher : user?.late_after);
  const exitFor = (k) => (k === "teacher" ? user?.exit_before_teacher : user?.exit_before);
  const [stats, setStats] = useState({ total_students: 0, today_records: 0, today_ins: 0, today_outs: 0 });
  const [week, setWeek] = useState(null);
  const [today, setToday] = useState([]);

  const load = async () => {
    try {
      const [s, w, t] = await Promise.all([
        api.get("/attendance/stats"),
        api.get("/attendance/week"),
        api.get("/attendance/today"),
      ]);
      setStats(s.data);
      setWeek(w.data);
      setToday(t.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);

  const chartData = (week?.by_day || []).map(d => ({
    day: DAYS_ES[new Date(d.date + "T00:00:00Z").getUTCDay()],
    entradas: d.ins,
    salidas: d.outs,
  }));

  return (
    <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
            <span className="w-8 h-px bg-navy" /> Resumen
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy">Información general</h1>
        </div>
        <LiveClock />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Alumnos" value={stats.total_students} icon={Users} testid="stat-students" />
        <StatCard label="Docentes" value={stats.total_teachers} icon={GraduationCap} testid="stat-teachers" />
        <StatCard label="Entradas hoy" value={stats.today_ins} icon={LogIn} testid="stat-ins" />
        <StatCard label="Salidas hoy" value={stats.today_outs} icon={LogOut} testid="stat-outs" />
        <StatCard label="Registros hoy" value={stats.today_records} icon={Activity} testid="stat-total" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display font-semibold text-lg text-navy">Esta semana</h3>
            <span className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Entradas vs. Salidas</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ left: -20 }}>
                <CartesianGrid stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="day" stroke="#7A7D80" fontSize={11} />
                <YAxis stroke="#7A7D80" fontSize={11} />
                <Tooltip contentStyle={{ border: "1px solid #C9CBCD", borderRadius: 6 }} />
                <Bar dataKey="entradas" fill="#112240" radius={[2,2,0,0]} />
                <Bar dataKey="salidas" fill="#1E3A8A" radius={[2,2,0,0]} fillOpacity={0.6} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy-light" />
          <h3 className="font-display font-semibold text-lg text-navy mb-4">Asistencia de hoy</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="today-feed">
            {(() => {
              // Group records by student_id, capturing first 'in' and last 'out'
              const grouped = {};
              today.forEach((r) => {
                if (!grouped[r.student_id]) {
                  grouped[r.student_id] = { id: r.student_id, name: r.student_name, kind: r.kind, in: null, out: null };
                }
                if (r.type === "in" && !grouped[r.student_id].in) grouped[r.student_id].in = r.timestamp;
                if (r.type === "out") grouped[r.student_id].out = r.timestamp;
              });
              const rows = Object.values(grouped).sort((a, b) => (b.out || b.in || "").localeCompare(a.out || a.in || ""));
              if (rows.length === 0) {
                return <div className="text-sm text-nardo-500 py-8 text-center">Sin registros aún. Ve a la Estación NFC.</div>;
              }
              return rows.slice(0, 40).map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-nardo-100 pb-2 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded ${p.kind === "teacher" ? "bg-navy-light/15 text-navy-light" : "bg-navy/10 text-navy"}`}>
                      {p.kind === "teacher" ? "M" : "A"}
                    </span>
                    <span className="text-sm font-medium text-navy truncate">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono shrink-0">
                    <span className={p.in ? (isLate(p.in, lateFor(p.kind)) ? "text-orange-600" : "text-emerald-700") : "text-nardo-300"} title={p.in && isLate(p.in, lateFor(p.kind)) ? "Entrada con retardo" : "Entrada"}>
                      ↓ {p.in ? fmtTime12(p.in) : "—"}
                    </span>
                    <span className={p.out ? (isEarly(p.out, exitFor(p.kind)) ? "text-violet-600" : "text-blue-600") : "text-nardo-300"} title={p.out && isEarly(p.out, exitFor(p.kind)) ? "Salida anticipada" : "Salida"}>
                      ↑ {p.out ? fmtTime12(p.out) : "—"}
                    </span>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
